import { useLocalization } from "@tm/localization"
import { Box, CellContentPosition, LicensePlate, TableCellData, TableColumnData } from "@tm/components"
import { VoucherType } from "@tm/models"
import { PropsWithChildren } from "react"
import { CostEstimationVoucher } from "../../../data/model"
import { VoucherDetailsButton } from "../../_shared/VoucherDetailsButton"
import { NoResultHint } from "../../_shared/NoResultHint"
import { StyledTableWithoutPadding } from "../../_shared/StyledComponents"
import CustomerInfo from "../../_shared/cost-estimations/CustomerInfo"

type Props = PropsWithChildren<{
    costEstimations: CostEstimationVoucher[]
    shortCountryCode: string
    showCustomerColumn?: boolean
}>

export function WidgetCostEstimationsList({ shortCountryCode, showCustomerColumn, costEstimations, ...rest }: Props) {
    const { formatDate } = useLocalization()

    function getColumns() {
        const columns: TableColumnData[] = [
            {}, // voucherNumber
            {}, // voucherCreationDate
        ]

        if (showCustomerColumn) {
            columns.push({}) // CustomerInfo
        }

        columns.push(
            {
                alignContent: CellContentPosition.right,
            } // LicensePlate
        )

        columns.push(
            {
                alignContent: CellContentPosition.left,
            }, // VehicleDescription
            { alignContent: CellContentPosition.right } // VoucherDetailsButton
        )
        return columns
    }

    function getCells(costEstimation: CostEstimationVoucher) {
        const data: TableCellData[] = [
            { displayValue: costEstimation.voucherNumber, id: "1" },
            { displayValue: formatDate(costEstimation.voucherCreationDate, "d"), id: "2" },
        ]

        if (showCustomerColumn) {
            data.push({ displayValue: <CustomerInfo costEstimation={costEstimation} />, id: "3", minWidth: 0 })
        }

        if (costEstimation.vehiclePlateId) {
            data.push({
                displayValue: <LicensePlate value={costEstimation.vehiclePlateId} countryCode={shortCountryCode} width="100%" />,
                id: "4",
            })
        } else {
            data.push({
                displayValue: "",
                id: "4",
            })
        }

        data.push(
            {
                displayValue: (
                    <Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{costEstimation.vehicleDescription}</Box>
                ),
                id: "5",
                minWidth: 0,
            },
            {
                displayValue: (
                    <VoucherDetailsButton
                        variant="small"
                        voucherId={costEstimation.id}
                        voucherWorkTaskId={costEstimation.workTaskId}
                        voucherType={VoucherType.CostEstimation}
                        buttonVariant="text"
                    />
                ),
                id: "6",
            }
        )
        return data
    }

    const displayData = costEstimations.map((costEstimation) => ({
        cells: getCells(costEstimation),
        id: `${costEstimation.id}`,
        customRow: false,
        active: false,
    }))

    if (!displayData.length) {
        return <NoResultHint />
    }

    return <StyledTableWithoutPadding variant="small" columns={getColumns()} rows={displayData} rowCap={4} headerStyle="hidden" {...rest} />
}
